import { useState, useRef } from "react";
import { ChevronDown, ChevronRight, Loader2, Copy, Check, Share2, Volume2, Square } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Flatten a completed Deep Analysis into a single plain-text block so it
 * can be copied to the clipboard or rendered onto the share PNG. Keeps
 * the sub-question tree structure explicit ("Q1./Q2./..." headers) and
 * appends the final synthesis under a "SYNTHESIS" separator.
 */
export function deepToText(deep) {
  if (!deep) return "";
  const parts = [];
  (deep.subs || []).forEach((s, i) => {
    if (s?.question) parts.push(`Q${i + 1}. ${s.question}`);
    if (s?.answer) parts.push(s.answer);
    parts.push("");
  });
  if (deep.synthesis) {
    parts.push("— SYNTHESIS —");
    parts.push(deep.synthesis);
  }
  return parts.join("\n").trim();
}

/**
 * Renders a Deep Analysis assistant message: the tree of 5 sub-Q&As
 * as collapsible items followed by the final synthesis.
 *
 * `deep` shape (from Chat.jsx sendDeep):
 *   {
 *     stage: "planning" | "answering" | "synthesizing" | "done" | "error",
 *     subs:  [{ question, answer, done }],
 *     synthesis: string,
 *     error?: string,
 *   }
 *
 * Copy + Share are exposed once `stage === "done"` so users can lift the
 * full tree + synthesis out of the app — same affordances as a standard
 * reply.
 */
export default function DeepAnalysisMessage({ deep, testId, onShare }) {
  const stage = deep?.stage || "planning";
  const subs = deep?.subs || [];
  const isDone = stage === "done";
  const [copied, setCopied] = useState(false);
  const [ttsState, setTtsState] = useState("idle");
  const audioRef = useRef(null);

  const onCopy = async () => {
    const text = deepToText(deep);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1300);
    } catch {
      toast.error("Copy failed");
    }
  };

  const onListen = async () => {
    if (ttsState === "playing" && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setTtsState("idle");
      return;
    }
    if (ttsState === "loading") return;
    // For Deep Analysis we voice ONLY the synthesis — reading out the
    // full Q1..Q5 + synthesis is ~4-8 minutes of audio and cuts against
    // the TTS quota. If the user wants a specific sub-answer, that's a
    // future feature.
    const text = (deep?.synthesis || "").trim();
    if (!text) {
      toast.error("Nothing to play yet.");
      return;
    }
    setTtsState("loading");
    try {
      const resp = await fetch(`${API}/tts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) {
        let msg = `TTS failed (${resp.status})`;
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch { /* empty */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setTtsState("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setTtsState("idle");
        URL.revokeObjectURL(url);
        audioRef.current = null;
        toast.error("Playback failed.");
      };
      setTtsState("playing");
      await audio.play();
    } catch (err) {
      setTtsState("idle");
      toast.error(err.message || "Couldn't generate voice.");
    }
  };

  return (
    <div className="max-w-none" data-testid={testId}>
      {/* Header pill */}
      <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 px-2.5 py-1 font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text)]">
        {(stage === "planning" || stage === "answering" || stage === "synthesizing") && (
          <Loader2 size={10} className="animate-spin" />
        )}
        Deep Analysis
        {stage === "planning" && " · planning"}
        {stage === "answering" && ` · answering (${subs.filter((s) => s.done).length}/${subs.length})`}
        {stage === "synthesizing" && " · synthesizing"}
        {stage === "done" && " · complete"}
        {stage === "error" && " · error"}
      </div>

      {/* Sub-question tree */}
      {subs.length > 0 && (
        <div className="mb-6 space-y-2">
          {subs.map((s, i) => (
            <DeepNode key={i} idx={i + 1} sub={s} defaultOpen={i === 0} />
          ))}
        </div>
      )}

      {/* Synthesis */}
      {deep?.synthesis && (
        <>
          <div className="rule-editorial my-4" />
          <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text)]/50 mb-2">
            Synthesis
          </div>
          <div className="font-prose text-[var(--text)] leading-relaxed whitespace-pre-wrap">
            {deep.synthesis}
            {stage === "synthesizing" && (
              <span className="inline-block w-1.5 h-4 -mb-0.5 ml-1 bg-white animate-pulse" />
            )}
          </div>
        </>
      )}

      {stage === "error" && (
        <div className="mt-3 text-[var(--text)]/70 text-sm">
          Something failed: {deep?.error || "unknown error"}
        </div>
      )}

      {/* Actions — same affordances as a standard reply (Copy + Share).
          Only rendered once the full tree + synthesis is complete so the
          copied/shared payload is never partial. */}
      {isDone && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`${testId}-actions`}>
          <button
            onClick={onCopy}
            className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] transition-colors"
            data-testid={`${testId}-copy`}
            aria-label={copied ? "Copied" : "Copy reply"}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
          {onShare && (
            <button
              onClick={() => onShare(deepToText(deep))}
              className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] transition-colors"
              data-testid={`${testId}-share`}
              aria-label="Share reply"
            >
              <Share2 size={12} />
              Share
            </button>
          )}
          <button
            onClick={onListen}
            disabled={ttsState === "loading" || !deep?.synthesis}
            className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] active:bg-white active:text-[var(--accent)] disabled:opacity-60 disabled:cursor-progress transition-colors"
            data-testid={`${testId}-listen`}
            aria-label={ttsState === "playing" ? "Stop" : "Listen to synthesis"}
            title="Reads the final synthesis aloud"
          >
            {ttsState === "loading" ? <Loader2 size={12} className="animate-spin" />
              : ttsState === "playing" ? <Square size={12} strokeWidth={2.5} fill="currentColor" />
              : <Volume2 size={12} />}
            {ttsState === "playing" ? "Stop" : ttsState === "loading" ? "Loading" : "Listen"}
          </button>
        </div>
      )}
    </div>
  );
}

function DeepNode({ idx, sub, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const answered = sub.done;

  return (
    <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--text)]/5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-[var(--text)]/5 transition-colors"
        data-testid={`deep-node-${idx}`}
      >
        <span className="mt-0.5 text-[var(--text)]/50">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text)]/50 shrink-0 mt-0.5">
          Q{idx}
        </span>
        <span className="flex-1 text-[var(--text)] text-sm leading-snug">{sub.question}</span>
        {!answered && (
          <Loader2 size={12} className="animate-spin text-[var(--text)]/50 mt-1 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1">
          {answered ? (
            <div className="font-prose text-[var(--text)]/90 text-sm leading-relaxed whitespace-pre-wrap pl-6">
              {sub.answer}
            </div>
          ) : (
            <div className="pl-6 text-[var(--text)]/50 text-xs italic">Thinking…</div>
          )}
        </div>
      )}
    </div>
  );
}
